import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { DEFAULT_OG_IMAGE, SITE_NAME, SITE_URL } from "../../seo/config";
import { getPageSeo } from "../../seo/pages";
import { organizationSchema, websiteSchema } from "../../seo/schemas";
import { useSeoOverrideValue } from "../../seo/SeoContext";

function upsertMeta(selector, attributes) {
    let element = document.head.querySelector(selector);
    if (!element) {
        element = document.createElement("meta");
        document.head.appendChild(element);
    }
    Object.entries(attributes).forEach(([key, value]) => {
        element.setAttribute(key, value);
    });
    return element;
}

function upsertLink(rel, href) {
    let element = document.head.querySelector(`link[rel="${rel}"]`);
    if (!element) {
        element = document.createElement("link");
        element.setAttribute("rel", rel);
        document.head.appendChild(element);
    }
    element.setAttribute("href", href);
}

function upsertJsonLd(id, data) {
    let element = document.getElementById(id);
    if (!element) {
        element = document.createElement("script");
        element.type = "application/ld+json";
        element.id = id;
        document.head.appendChild(element);
    }
    element.textContent = JSON.stringify(data);
}

export default function SeoHead() {
    const { pathname } = useLocation();
    const override = useSeoOverrideValue();
    const seo = override || getPageSeo(pathname);
    const canonicalUrl = seo.canonicalUrl || `${SITE_URL}${pathname === "/" ? "" : pathname}`;
    const robots = seo.robots || "index, follow";
    const ogImage = seo.image || DEFAULT_OG_IMAGE;

    useEffect(() => {
        document.title = seo.title;

        upsertMeta('meta[name="description"]', {
            name: "description",
            content: seo.description,
        });

        if (seo.keywords) {
            upsertMeta('meta[name="keywords"]', {
                name: "keywords",
                content: seo.keywords,
            });
        }

        upsertMeta('meta[name="robots"]', {
            name: "robots",
            content: robots,
        });

        upsertLink("canonical", canonicalUrl);

        upsertMeta('meta[property="og:type"]', {
            property: "og:type",
            content: "website",
        });
        upsertMeta('meta[property="og:site_name"]', {
            property: "og:site_name",
            content: SITE_NAME,
        });
        upsertMeta('meta[property="og:title"]', {
            property: "og:title",
            content: seo.title,
        });
        upsertMeta('meta[property="og:description"]', {
            property: "og:description",
            content: seo.description,
        });
        upsertMeta('meta[property="og:url"]', {
            property: "og:url",
            content: canonicalUrl,
        });
        upsertMeta('meta[property="og:image"]', {
            property: "og:image",
            content: ogImage,
        });
        upsertMeta('meta[property="og:locale"]', {
            property: "og:locale",
            content: "en_IN",
        });

        upsertMeta('meta[name="twitter:card"]', {
            name: "twitter:card",
            content: "summary_large_image",
        });
        upsertMeta('meta[name="twitter:title"]', {
            name: "twitter:title",
            content: seo.title,
        });
        upsertMeta('meta[name="twitter:description"]', {
            name: "twitter:description",
            content: seo.description,
        });
        upsertMeta('meta[name="twitter:image"]', {
            name: "twitter:image",
            content: ogImage,
        });

        upsertJsonLd("schema-organization", organizationSchema());
        upsertJsonLd("schema-website", websiteSchema());

        if (seo.schemas?.length > 0) {
            const payload =
                seo.schemas.length === 1 ? seo.schemas[0] : seo.schemas;
            upsertJsonLd("schema-page", payload);
        } else {
            document.getElementById("schema-page")?.remove();
        }
    }, [canonicalUrl, robots, seo, ogImage]);

    return null;
}
